<template>
    <div>
        <kgr-loader v-show="loading" />

        <div
            v-show="!loading"
            class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters"
        >
            <div class="kgr-grid__cell">
                <kgr-form-row
                    :label="$t('Debitorennummer')"
                    :error="accountsReceivableNumberError"
                    field-name="AccountsReceivableNumber"
                >
                    <kgr-number-input
                        v-model="accountsReceivableNumber.counter"
                        :disabled="accountsReceivableNumber.counter === null && initialCounter === null"
                        allow-null
                    />
                    <kgr-checkbox
                        v-if="accountsReceivableNumber.counter === null && initialCounter === null"
                        v-model="createAccountsReceivableNumber"
                        :label="$t('Eine Debitorennummer gemäß den Einstellungen des Nummernkreises generieren')"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Zahlungsintervall')"
                    :error="error"
                    field-name="paymentInterval"
                >
                    <kgr-generic-select
                        v-model="paymentInterval"
                        :options="paymentIntervals"
                        display="text"
                        @post-input="updateDefaultPaymentMethod()"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Standard-Zahlart')"
                    :error="error"
                    field-name="defaultPaymentMethod"
                    :tooltip="$t('Die Standard-Zahlung wird bei allen Beiträgen, die in Zukunft angelegt werden berücksichtigt. Bereits angelegte Beiträge, auch wenn diese erst in der Zukunft starten, müssen manuell angepasst werden')"
                >
                    <kgr-generic-select
                        v-model="defaultPaymentMethod"
                        :options="computedPaymentMethods"
                        display="text"
                        @post-input="updateDefaultPaymentMethod()"
                    />
                </kgr-form-row>
                <div
                    v-if="otherPaymentReceiverSelected && isUsedAsDefaultInvoiceReceiver"
                    class="error--text"
                >
                    {{ $t('Das Mitglied kann keinen abweichenden Rechnungsempfänger wählen, da es selbst als abweichender Rechnungsempfänger ausgewählt ist.') }}
                </div>
                <kgr-form-row
                    v-if="otherPaymentReceiverSelected"
                    :label="$t('Rechnungsempfänger')"
                    :error="invoiceReceiverError"
                    field-name="invoiceReceiver"
                >
                    <kgr-member-relation-select
                        v-model="defaultInvoiceReceiver"
                        :disabled="isUsedAsDefaultInvoiceReceiver"
                        :include-self="false"
                        :member-id="id"
                        @post-input="updateDefaultInvoiceReceiver($event)"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Keine weiteren Rechnungen für dieses Mitglied erstellen')"
                    :error="error"
                    field-name="owner"
                >
                    <kgr-checkbox v-model="isDunningActive" />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Keine weiteren Mahnungen für dieses Mitglied erstellen')"
                    :error="error"
                    field-name="owner"
                >
                    <kgr-checkbox v-model="isDunningStopActive" />
                </kgr-form-row>
            </div>
            <div class="kgr-grid__cell">
            </div>
            <div class="kgr-row">
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="isUsedAsDefaultInvoiceReceiver && otherPaymentReceiverSelected"
                    @click="update()"
                >
                    {{ $t('Speichern') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import AccountsReceivableNumber from '../../../js/dto/AccountsReceivableNumber.js';
import AccountsReceivableNumberConfig from '../../../js/dto/AccountsReceivableNumberConfig.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import Error from '../../../js/model/Error.js';
// import ErrorHandler from '../ErrorHandler';
import FormRow from '../../../components/common/form/FormRow.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Loading from '../../../components/common/Loading.vue';
import MemberRelationSelect from '../../../components/common/form/select/MemberRelationSelect.vue';
import NumberInput from '../../../components/common/form/input/NumberInput.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-loader': Loading,
        'kgr-generic-select': GenericSelect,
        'kgr-checkbox': Checkbox,
        'kgr-form-row': FormRow,
        'kgr-number-input': NumberInput,
        'kgr-member-relation-select': MemberRelationSelect,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            error: new Error(),
            paymentInterval: null,
            initialCounter: null,
            isDunningActive: false,
            isDunningStopActive: false,
            paymentIntervals: [
                { value: 'monthly', text: this.$t('monatlich') },
                { value: 'quarterly', text: this.$t('quartalsweise') },
                { value: 'halfYearly', text: this.$t('halbjährlich') },
                { value: 'yearly', text: this.$t('jährlich') },
            ],
            paymentMethods: [
                { value: 'undefined', text: this.$t('Undefiniert') },
                { value: 'cash', text: this.$t('Bar') },
                { value: 'debit-card', text: this.$t('Kartenzahlung') },
                { value: 'bank-transfer', text: this.$t('Überweisung') },
                { value: 'sepa-debit', text: this.$t('SEPA-Lastschrift') },
                { value: 'other-invoice-receiver', text: this.$t('Abweichender Rechnungsempfänger') },
            ],
            defaultPaymentMethod: { value: 'undefined', text: this.$t('Undefiniert') },
            defaultInvoiceReceiver: null,
            isUsedAsDefaultInvoiceReceiver: false,
            invoiceReceivers: null,
            defaultPaymentMethodChanged: false,
            sepaMandateAvailable: null,
            accountsReceivableNumber: new AccountsReceivableNumber(),
            accountsReceivableNumberConfig: new AccountsReceivableNumberConfig(),
            createAccountsReceivableNumber: false,
        };
    },
    computed: {
        otherPaymentReceiverSelected() {
            return this.defaultPaymentMethod === 'other-invoice-receiver' || this.defaultPaymentMethod?.value === 'other-invoice-receiver';
        },
        computedPaymentMethods() {
            return this.paymentMethods.map((item) => ({
                ...item,
                disabled: !this.sepaMandateAvailable && item.value === 'sepa-debit',
            }));
        },
        accountsReceivableNumberDeletable() {
            return this.initialCounter === null || this.accountsReceivableNumber.deletable === true;
        },
        accountsReceivableNumberError() {
            const error = new Error();
            if ((!this.accountsReceivableNumberDeletable && this.accountsReceivableNumber.counter === null) || this.error.messageForField('AccountsReceivableNumber.delete') != null) {
                error.add('AccountsReceivableNumber', this.$t('Die Debitorennummer kann nicht gelöscht werden, weil bereits Beiträge angelegt sind.'));
            }
            if (this.error.messageForField('AccountsReceivableNumber.numberExists') != null) {
                error.add('AccountsReceivableNumber', this.$t('Diese Debitorennummer ist bereits vergeben.'));
            }
            if (this.error.messageForField('AccountsReceivableNumber.maxCounterReached') != null) {
                error.add('AccountsReceivableNumber', this.$t('Der Nummernkreis für Debitorennummern ist erschöpft und erlaubt keine weiteren Einträge.'));
            }
            return error;
        },
        invoiceReceiverError() {
            const error = new Error();
            if (this.error.messageForField('invoiceReceiver') != null) {
                error.add('invoiceReceiver', this.$t('Ein Mitglied kann nicht sein eigener Rechnungsempfänger sein.'));
            }
            return error;
        },
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            axios
                .get(`/club/members/${this.id}/settings`)
                .then((response) => {
                    this.isDunningActive = response.data.data.isDunningActive;
                    this.isDunningStopActive = response.data.data.isDunningStopActive;
                    this.defaultInvoiceReceiver = response.data.data.defaultInvoiceReceiver;
                    this.isUsedAsDefaultInvoiceReceiver = response.data.data.isUsedAsDefaultInvoiceReceiver;
                    this.sepaMandateAvailable = response.data.data.sepaMandateAvailable;
                    const defaultPaymentMethodValue = response.data.data.defaultPaymentMethod;
                    let defaultPaymentMethod = null;
                    if (defaultPaymentMethodValue !== null) {
                        this.computedPaymentMethods.forEach((element) => {
                            if (element.value === defaultPaymentMethodValue) {
                                defaultPaymentMethod = element;
                            }
                        });
                    }
                    if (defaultPaymentMethod !== null) {
                        this.defaultPaymentMethod = defaultPaymentMethod;
                    }
                    const paymentIntervalValue = response.data.data.paymentInterval.data.type;
                    let paymentInterval = null;
                    if (paymentIntervalValue !== null) {
                        this.paymentIntervals.forEach((element) => {
                            if (element.value === paymentIntervalValue) {
                                paymentInterval = element;
                            }
                        });
                    }
                    if (paymentInterval !== null) {
                        this.paymentInterval = paymentInterval;
                    }
                    if (response.data.data.accountsReceivableNumber) {
                        this.accountsReceivableNumber = AccountsReceivableNumber.fromData(response.data.data.accountsReceivableNumber);
                    } else {
                        this.accountsReceivableNumber = new AccountsReceivableNumber(null);
                    }
                    this.accountsReceivableNumberConfig = AccountsReceivableNumberConfig.fromData(response.data.data.accountsReceivableNumberConfig);
                    this.initialCounter = this.accountsReceivableNumber?.counter;
                    this.loading = false;
                });
        },
        updateDefaultPaymentMethod() {
            this.defaultPaymentMethodChanged = true;
        },
        updateDefaultInvoiceReceiver() {
            this.defaultPaymentMethodChanged = true;
        },
        update() {
            axios
                .put(`/club/members/${this.id}/settings`, this.createPayload(), {
                    // headers: new ErrorHandler().enableCustomErrorHandling({})
                })
                .then(() => {
                    this.error = new Error();
                    this.load();
                })
                .catch((error) => {
                    this.error = Error.fromResponse(error.response);
                    if (this.error.messageForField('AccountsReceivableNumber.maxCounterReached')) {
                        this.$notify.error('Es kann keine Debitorennummer mehr generiert werden, weil der Nummernkreis zu klein ist.');
                    } else if (!this.error.messageForField('AccountsReceivableNumber.numberExists') && !this.error.messageForField('AccountsReceivableNumber.delete') && !this.error.messageForField('invoiceReceiver')) {
                        this.$notify.error(this.error.message);
                    }
                })
            ;
        },
        onAccountsReceivableNumberCreateFlagChanged(create) {
            this.createAccountsReceivableNumber = create;
        },
        createPayload() {
            return {
                isDunningActive: this.isDunningActive,
                isDunningStopActive: this.isDunningStopActive,
                paymentInterval: this.createPaymentIntervalPayload(),
                defaultPaymentMethod: this.defaultPaymentMethodChanged ? this.defaultPaymentMethod.value : null,
                defaultInvoiceReceiver: (this.otherPaymentReceiverSelected ? this.defaultInvoiceReceiver?.id : null) || null,
                accountsReceivableNumber: this.accountsReceivableNumber.serialize(),
                createAccountsReceivableNumber: this.createAccountsReceivableNumber,
            };
        },
        createPaymentIntervalPayload() {
            switch (this.paymentInterval.value) {
                case 'monthly':
                    return {
                        type: this.paymentInterval.value,
                        day: 1,
                    };

                case 'quarterly':
                    return {
                        type: this.paymentInterval.value,
                        offsetDay: 0,
                        offsetMonth: 0,
                    };

                case 'halfYearly':
                    return {
                        type: this.paymentInterval.value,
                        offsetDay: 0,
                        offsetMonth: 0,
                    };

                case 'yearly':
                    return {
                        type: this.paymentInterval.value,
                        day: 1,
                        month: 1,
                    };
            }
            throw new Error(`unknown interval "${this.paymentInterval}"`);
        },
    },
};
</script>
